import React, { useCallback, useEffect, useState } from "react";
import { Formik, Form, useFormikContext } from "formik";
import { useSelector } from "react-redux";
import { RootState } from "@store/index";
import Column from "@components/column";
import Row from "@components/row";
import Fields from "@components/fields";
import DropDown from "@components/fields/dropdown";
import DateTimePicker from "@components/fields/datetime";
import ButtonWrapper from "@components/wrapper/button-wrapper";
import Button from "@components/button";
import { Coupon, Store, Zone, Category } from "@apis/index";
import ModuleDropdown from "@commons/modules/dropdown";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";

const CouponForm = ({
  role,
  state: propsState,
}: {
  role: string;
  state?: any;
}) => {
  const navigate = useNavigate();
  let { data: user } = useSelector((state: RootState) => state.user);
  const { state } = useLocation();
  if (propsState) {
    user = propsState;
  }

  console.log({ state, propsState });
  const [initialValues, setInitialValues] = useState({
    title: "",
    module: user?.role === "store" ? user?.module?.id : "",
    type: "store",
    category: "",
    store: user?.role === "store" ? user?.id : "",
    zone: "",
    startDate: "",
    endDate: "",
    code: "",
    usagePerUser: "5",
    totalUsage: "1",
    discountType: "",
    discountAmount: "",
    minPurchase: "",
    maxDiscount: "",
    bearer: "",
    admin: "",
    vendor: "",
    isShow: false,
  });

  const { items: stores } = useSelector((state: RootState) => state.stores);
  const { data: zones } = useSelector((state: RootState) => state.zones);
  const { items: categories } = useSelector(
    (state: RootState) => state.categories,
  );

  const [loading, setLoading] = useState(false);

  const fetchStores = useCallback(
    async (moduleId?: any) =>
      await Store.list({
        state: "stores",
        params: { moduleId: moduleId || undefined, limit: 3000 },
      }),
    [],
  );
  const fetchZones = useCallback(
    async (moduleId?: any) =>
      await Zone.list({
        state: "zones",
        params: { moduleId: moduleId || undefined, limit: 3000 },
      }),
    [],
  );
  const fetchCategories = useCallback(
    async (moduleId?: any) =>
      await Category.list({
        state: "categories",
        params: { moduleId: moduleId || undefined, limit: 3000 },
      }),
    [],
  );

  useEffect(() => {
    fetchStores(initialValues.module);
    fetchZones(initialValues.module);
    fetchCategories(initialValues.module);
  }, [fetchStores, fetchZones, fetchCategories, initialValues.module]);

  const fetchCoupons = useCallback(async () => {
    setLoading(true);
    try {
      await Coupon.list({
        state: user?.role + "Coupons",
        params: {
          role: user?.role,
          ...(user?.role === "store" && { storeId: user?.id }),
        },
      });
    } catch (err: any) {
      toast.error(err?.message || "Failed to fetch coupons");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    if (!state?.id) {
      setInitialValues({
        title: "",
        module: user?.role === "store" ? user?.module?.id : "",
        type: "store",
        category: "",
        store: user?.role === "store" ? user?.id : "",
        zone: "",
        startDate: "",
        endDate: "",
        code: "",
        usagePerUser: "5",
        totalUsage: "1",
        discountType: "",
        discountAmount: "",
        minPurchase: "",
        maxDiscount: "",
        bearer: "",
        admin: "",
        vendor: "",
        isShow: false,
      });
      return;
    }
    setInitialValues({
      title: state.title,
      module: state.module?.id,
      type: state.couponType,
      category: state.categoryId || "",
      store: state?.store?.id || propsState?.id,
      zone: state.zone?.id || "",
      startDate: state.startDate,
      endDate: state.endDate,
      code: state.code,
      usagePerUser: state.usagePerUser,
      totalUsage: state.totalUsage,
      discountType: state.discountInfos?.type || "",
      discountAmount: state.discountInfos?.amount || "",
      minPurchase: state.discountInfos?.minPurchase || "",
      maxDiscount: state.discountInfos?.maxDiscount || "",
      bearer: state.payBy?.type || "",
      admin: state.payBy?.admin || "",
      vendor: state.payBy?.vendor || "",
      isShow: state.isShow ?? false,
    });
    if (state?.module?.id) {
      fetchZones(state.module.id);
    }
  }, [state]);

  const handleSubmit = async (
    values: typeof initialValues,
    { resetForm }: any,
  ) => {
    const payload: any = {
      title: values.title,
      moduleId: Number(values.module),
      couponType: values.type,
      startDate: values.startDate,
      endDate: values.endDate,
      code: values.code,
      usagePerUser: Number(values.usagePerUser),
      totalUsage: Number(values.totalUsage),
      role: user?.role,
      discountInfos: {
        type: values.discountType,
        amount: Number(values.discountAmount),
        minPurchase: Number(values.minPurchase),
        maxDiscount: Number(values.maxDiscount),
      },
      payBy: {
        type: user?.role === "store" ? "vendor" : values.bearer,
      },
      isActive: true,
      isShow: values.isShow === true || String(values.isShow) === "true",
    };
    if (
      user?.role === "store" ||
      user?.role === "vendor" ||
      propsState?.role === "store" ||
      propsState?.role === "vendor"
    ) {
      payload.storeId = Number(values.store);
    }
    if (values.type === "store") {
      payload.storeId = Number(values.store);
    }
    if (values.type === "zone") {
      payload.zoneId = Number(values.zone);
    }
    if (values.type === "category") {
      payload.categoryId = Number(values.category);
    }
    if (values.bearer === "partial") {
      payload.payBy.admin = Number(values.admin);
    }
    if (values.bearer === "partial") {
      payload.payBy.vendor = Number(values.vendor);
    }
    setLoading(true);
    const action = !!state?.code
      ? Coupon.update({
          state: user?.role + "Coupons",
          data: payload,
          id: Number(state?.id),
        })
      : Coupon.create({ state: user?.role + "Coupons", data: payload });

    toast.promise(action, {
      pending: state?.id ? "Updating coupon..." : "Creating coupon...",
      success: state?.id
        ? "Coupon updated successfully!"
        : "Coupon created successfully!",
      error: "Failed to Create Coupon",
    });
    fetchCoupons();
    if (!!state?.id) {
      // navigate(-1);
    }
    try {
      await action;
      // resetForm(initialValues);
    } finally {
      setLoading(false);
    }
  };

  const StoreZoneField = ({ disabled }: { disabled?: boolean }) => {
    const { values } = useFormikContext<typeof initialValues>();

    // Show Store by default, switch to Zone if type === "zone"
    return values.type === "category" ? (
      <DropDown
        name="category"
        label="Category"
        options={categories || []}
        titleAsKey="name"
        valueAsKey="id"
        placeholder="---Select Category---"
        disabled={disabled}
        styleInput="width:96%"
      />
    ) : values.type === "zone" ? (
      <DropDown
        name="zone"
        label="Zone"
        options={zones || []}
        titleAsKey="name"
        valueAsKey="id"
        placeholder="---Select Zone---"
        disabled={disabled}
        styleInput="width:96%"
      />
    ) : values.type === "store" ? (
      <DropDown
        name="store"
        label="Store"
        options={stores || []}
        titleAsKey="storeName"
        valueAsKey="id"
        placeholder="---Select Store---"
        styleInput="width:96%"
        disabled={user?.role === "store"}
        hasSearch
      />
    ) : null;
  };

  const BearerFields = () => {
    const { values } = useFormikContext<typeof initialValues>();
    return (
      <Row styleRow="justify-content:flex-start;width:100%">
        <Fields.Input
          name="admin"
          label="Admin Percentage"
          placeholder="Enter percentage for admin"
          disabled={values.bearer !== "partial"}
        />
        <Fields.Input
          name="vendor"
          label="Vendor Percentage"
          placeholder="Enter percentage for vendor"
          disabled={values.bearer !== "partial"}
        />
      </Row>
    );
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize={true}
      onSubmit={handleSubmit}
    >
      {({ setFieldValue, values }) => (
        <Form>
          <Column>
            <Fields.Input
              name="title"
              label="Coupon Title"
              placeholder="Enter title"
            />
            <Row>
              <ModuleDropdown
                name="module"
                styleInput="width:96%"
                disabled={user?.role === "store"}
                onChange={(val) => {
                  setFieldValue("module", val);
                  setFieldValue("store", "");
                  setFieldValue("zone", "");
                  setFieldValue("category", "");
                  fetchStores(val);
                  fetchZones(val);
                  fetchCategories(val);
                }}
              />
              {user?.role === "store" ? (
                <DropDown
                  name="type"
                  label="Coupon Type"
                  options={[
                    { title: "Store Wise", value: "store" },
                    { title: "Free Delivery", value: "freeDelivery" },
                    { title: "First Order", value: "firstOrder" },
                    { title: "Default", value: "default" },
                  ]}
                  titleAsKey="title"
                  valueAsKey="value"
                  placeholder="---Select Coupon Type---"
                  styleInput="width:96%"
                />
              ) : (
                <DropDown
                  name="type"
                  label="Coupon Type"
                  options={[
                    { title: "Store Wise", value: "store" },
                    { title: "Zone Wise", value: "zone" },
                    { title: "Category Wise", value: "category" },
                    { title: "Free Delivery", value: "freeDelivery" },
                    { title: "First Order", value: "firstOrder" },
                    { title: "Default", value: "default" },
                  ]}
                  titleAsKey="title"
                  valueAsKey="value"
                  placeholder="---Select Coupon Type---"
                  styleInput="width:96%"
                />
              )}

              <StoreZoneField disabled={!values.module} />
            </Row>
            <Row>
              <Fields.Input
                name="code"
                label="Coupon Code"
                placeholder="Enter code"
              />
              <Fields.Input
                name="usagePerUser"
                placeholder="EX:10"
                label="Usage Limit per User"
              />
              <Fields.Input
                name="totalUsage"
                placeholder="EX:10 (set 0 for unlimited usage)"
                label="Total Usage Limit"
              />
            </Row>
            {user?.role === "store" && (
              <Row>
                <DateTimePicker name="startDate" label="Start Date" required />
                <DateTimePicker name="endDate" label="End Date" required />
              </Row>
            )}

            <Row>
              <DropDown
                name="discountType"
                label="Discount Type"
                options={[
                  { id: "percentage", name: "Percentage" },
                  { id: "flat", name: "Flat Amount" },
                  // { id: "bogo", name: "Buy One Get One" },
                ]}
                titleAsKey="name"
                valueAsKey="id"
                placeholder="---Select Discount Type---"
                styleInput="width:96%"
              />
              <Fields.Input
                name="discountAmount"
                label="Discount Amount"
                placeholder="0"
              />
              <Fields.Input
                name="minPurchase"
                label="Min Purchase"
                placeholder="0"
              />
              <Fields.Input
                name="maxDiscount"
                label="Max Discount"
                placeholder="0"
              />
            </Row>

            {user?.role === "admin" && (
              <Row styleRow="align-items:center">
                <DateTimePicker name="startDate" label="Start Date" required />
                <DateTimePicker name="endDate" label="End Date" required />
                <Fields.GroupRadioBox
                  name="bearer"
                  label="Who Will Pay?"
                  options={[
                    { id: "admin", title: "Admin" },
                    { id: "vendor", title: "Vendor" },
                    { id: "partial", title: "Partial" },
                  ]}
                  titleAsKey="title"
                  valueAsKey="id"
                />
                <BearerFields />
              </Row>
            )}

            <Row>
              <Fields.GroupRadioBox
                name="isShow"
                label="Show Promocode"
                options={[
                  { id: true, title: "Yes" },
                  { id: false, title: "No" },
                ]}
                titleAsKey="title"
                valueAsKey="id"
              />
            </Row>

            <ButtonWrapper>
              <Button
                title="Reset"
                type="reset"
                variant="reset"
                disabled={loading}
              />
              <Button
                title={!!state?.code ? "Update" : "Submit"}
                type="submit"
                disabled={loading}
              />
            </ButtonWrapper>
          </Column>
        </Form>
      )}
    </Formik>
  );
};

export default CouponForm;
